import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API, pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Empty, Spinner } from "@/components/common";
import { toast } from "sonner";
import { Sparkles, Loader2, Code2, Download, ArrowRight, Plug, FileCode2, Play, CheckCircle2, XCircle, Info } from "lucide-react";

export default function IntegrationsPanel({ pid, meetings }) {
  const { data: interfaces, refetch, isLoading } = useQuery({ queryKey: ["interfaces", pid], queryFn: async () => (await api.get(`/projects/${pid}/interfaces`)).data });
  const { data: connectors } = useQuery({ queryKey: ["connectors"], queryFn: async () => (await api.get("/connectors")).data });
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null);
  const [codeFor, setCodeFor] = useState(null);
  const [runFor, setRunFor] = useState(null);
  const analyzed = meetings.find((m) => m.status === "analyzed") || meetings[0];

  const derive = async () => {
    if (!analyzed) { toast.error("Create and analyze a meeting first"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("meeting_id", analyzed.id);
    try { const res = await api.post(`/projects/${pid}/interfaces/derive`, fd, { timeout: 60000 }); const r = await pollJob(res.data.job_id); toast.success(`Identified ${r.created} interface(s)`); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message || "Failed"); } finally { setBusy(false); }
  };

  const generateAllCode = async () => {
    setBusy(true);
    try {
      const r = (await api.post(`/projects/${pid}/interfaces/generate-all-code`)).data;
      toast.success(r.generated ? `Generated starter code for ${r.generated} interface(s)` : "All interfaces already have starter code");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Failed"); } finally { setBusy(false); }
  };

  const viewCode = async (itf) => {
    setCodeFor(itf); setCode(null);
    const res = await api.get(`/interfaces/${itf.id}/code`);
    setCode(res.data.files);
  };

  const link = async (itf, connector_id) => {
    try { await api.put(`/interfaces/${itf.id}/connector`, { connector_id: connector_id || null }); toast.success(connector_id ? "Connector linked" : "Connector unlinked"); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to link"); }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-muted-foreground">{(interfaces || []).length} interface(s) · pattern + starter code · run against a linked SAP connector</div>
        <div className="flex items-center gap-2 flex-wrap">
          {(interfaces || []).length > 0 && (
            <Button onClick={generateAllCode} disabled={busy} variant="outline" className="gap-2" data-testid="generate-all-code"><FileCode2 size={15} /> Generate code for all</Button>
          )}
          <Button onClick={derive} disabled={busy} className="gap-2" data-testid="derive-interfaces">{busy ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />} Derive interfaces from requirements</Button>
        </div>
      </div>

      <div className="text-xs rounded-lg border border-[#c9a227]/40 bg-[#c9a227]/10 p-3 text-muted-foreground flex items-start gap-2" data-testid="integrations-preview-note">
        <Info size={14} className="text-[#c9a227] mt-0.5 shrink-0" />
        <span><span className="font-medium text-foreground">Preview stage.</span> Generated projects are reviewed <span className="font-medium text-foreground">starter kits</span> and live runs call SAP APIs for testing only. Business360 does <span className="font-medium text-foreground">not</span> perform an SAP transport or auto-deploy into your landscape yet — review and deploy generated code through your own SAP change process.</span>
      </div>

      {!connectors?.length && (interfaces || []).length > 0 && (
        <div className="text-xs rounded-lg border border-dashed p-3 text-muted-foreground">No SAP connectors configured yet. Add one under <span className="font-medium text-foreground">SAP Connectors</span> in the sidebar to run these interfaces live.</div>
      )}

      {!interfaces?.length ? (
        <Empty title="No interfaces yet" hint="The AI reads your requirements and landscape to design SAP/CRM integration cards, recommend a pattern and generate starter code." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {interfaces.map((itf) => (
            <div key={itf.id} className="rounded-xl border bg-card p-5 card-hover" data-testid={`interface-${itf.id}`}>
              <div className="flex items-center gap-2 text-[#0f2557] dark:text-[#29abe2]"><Plug size={16} /><span className="font-semibold text-foreground">{itf.name}</span></div>
              <div className="flex items-center gap-2 text-sm mt-3">
                <span className="bg-secondary px-2 py-1 rounded text-xs font-medium">{itf.source}</span>
                <ArrowRight size={14} className="text-[#29abe2]" />
                <span className="bg-secondary px-2 py-1 rounded text-xs font-medium">{itf.target}</span>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs mt-3">
                {[["Pattern", itf.pattern], ["Direction", itf.direction], ["Trigger", itf.trigger], ["Artifact", itf.sap_artifact], ["Auth", itf.authentication], ["Middleware", itf.middleware]].map(([k, v]) => (
                  <div key={k}><dt className="text-muted-foreground">{k}</dt><dd className="text-foreground font-medium truncate">{v || "—"}</dd></div>
                ))}
              </dl>
              {itf.field_mapping?.length > 0 && (
                <div className="mt-3 text-xs">
                  <div className="text-muted-foreground mb-1">Field mapping ({itf.field_mapping.length})</div>
                  <div className="space-y-0.5 max-h-20 overflow-y-auto">
                    {itf.field_mapping.slice(0, 4).map((f, i) => <div key={i} className="font-mono text-[11px] text-foreground">{f.source} → {f.target} <span className="text-muted-foreground">({f.rule})</span></div>)}
                  </div>
                </div>
              )}

              {/* Connector link */}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-muted-foreground shrink-0">SAP connector</span>
                <Select value={itf.connector_id || "none"} onValueChange={(v) => link(itf, v === "none" ? null : v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`link-connector-${itf.id}`}><SelectValue placeholder="Not linked" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not linked</SelectItem>
                    {(connectors || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.call_style})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 mt-4 flex-wrap">
                <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => viewCode(itf)} data-testid={`code-${itf.id}`}><Code2 size={13} /> View code</Button>
                <a href={`${API}/interfaces/${itf.id}/download`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1.5 h-8"><Download size={13} /> Download .zip</Button></a>
                <Button size="sm" variant="outline" className="gap-1.5 h-8" disabled={!itf.connector_id} onClick={() => setRunFor(itf)} data-testid={`run-interface-${itf.id}`}><Play size={13} /> Run (preview)</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <RunInterfaceDialog interface={runFor} connectors={connectors || []} onClose={() => setRunFor(null)} />

      <Dialog open={!!codeFor} onOpenChange={() => setCodeFor(null)}>
        <DialogContent className="max-w-3xl h-[82vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileCode2 size={18} /> {codeFor?.name} — Starter Kit <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#c9a227]/15 text-[#a6821a]">PREVIEW · not a live transport</span></DialogTitle>
          </DialogHeader>
          {!code ? <Spinner label="Generating code…" /> : (
            <div className="overflow-y-auto space-y-4 pr-2">
              {Object.entries(code).map(([name, content]) => (
                <div key={name}>
                  <div className="text-xs font-mono font-semibold text-[#29abe2] mb-1">{name}</div>
                  <pre className="text-[11px] bg-[#0b1329] text-[#dbe4f5] rounded-lg p-3 overflow-x-auto font-mono leading-relaxed">{content}</pre>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RunInterfaceDialog({ interface: itf, connectors, onClose }) {
  const [op, setOp] = useState({ method: "GET", entity: "", payload: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const conn = connectors.find((c) => c.id === itf?.connector_id);

  React.useEffect(() => {
    if (itf) {
      const sample = {};
      (itf.field_mapping || []).forEach((f) => { if (f.target) sample[String(f.target).split(".").pop()] = ""; });
      setOp({ method: "POST", entity: itf.sap_artifact || "", payload: Object.keys(sample).length ? JSON.stringify(sample, null, 2) : "" });
      setResult(null);
    }
  }, [itf]);
  if (!itf) return null;

  const run = async () => {
    setBusy(true); setResult(null);
    let payload = op.payload;
    if (op.method !== "GET" && op.payload) { try { payload = JSON.parse(op.payload); } catch { toast.error("Payload must be valid JSON"); setBusy(false); return; } }
    try {
      const r = (await api.post(`/interfaces/${itf.id}/run`, { method: op.method, entity: op.entity, payload })).data;
      setResult(r);
    } catch (e) { setResult({ ok: false, detail: e?.response?.data?.detail || "Run failed" }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!itf} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="run-interface-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Play size={16} /> Run “{itf.name}”</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-muted-foreground">Via connector <span className="font-medium text-foreground">{conn?.name || itf.connector_id}</span> ({conn?.call_style}) · payload prefilled from the field mapping.</div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Method</Label>
              <Select value={op.method} onValueChange={(v) => setOp({ ...op, method: v })}>
                <SelectTrigger data-testid="itf-run-method"><SelectValue /></SelectTrigger>
                <SelectContent>{["GET", "POST", "PATCH", "PUT"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Entity / artifact</Label><Input value={op.entity} onChange={(e) => setOp({ ...op, entity: e.target.value })} data-testid="itf-run-entity" /></div>
          </div>
          {op.method !== "GET" && <div><Label>Payload (JSON)</Label><Textarea rows={6} value={op.payload} onChange={(e) => setOp({ ...op, payload: e.target.value })} className="font-mono text-xs" data-testid="itf-run-payload" /></div>}
          <Button onClick={run} disabled={busy} className="gap-2" data-testid="itf-run-execute">{busy ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />} Execute</Button>
          {result && (
            <div className="rounded-lg border p-3 space-y-2" data-testid="itf-run-result">
              <div className={`flex items-center gap-1.5 text-sm font-medium ${result.ok ? "text-emerald-600" : "text-rose-600"}`}>
                {result.ok ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                {result.ok ? `Success${result.http_status ? ` · HTTP ${result.http_status}` : ""}` : "Failed"}{result.latency_ms != null ? ` · ${result.latency_ms} ms` : ""}
              </div>
              {result.detail && <div className="text-xs text-muted-foreground">{result.detail}</div>}
              {result.response !== undefined && <pre className="text-[11px] bg-[#0b1329] text-[#dbe4f5] rounded-lg p-3 overflow-x-auto font-mono max-h-64">{typeof result.response === "string" ? result.response : JSON.stringify(result.response, null, 2)}</pre>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
